import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import { isValidDashboardHash } from '../utils/hash';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface SurveyResponse {
  experiencia_programacao: string;
  desafios: string[];
  objetivo_principal: string;
  fator_bloqueio: string;
  preferencia_notificacao: string;
  lancamento: number;
}

interface ChartData {
  name: string;
  value: number;
  percentage: number;
}

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [expandedTexts, setExpandedTexts] = useState<boolean>(false);
  const [selectedLancamento, setSelectedLancamento] = useState<number>(1);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const searchParams = new URLSearchParams(location.search);
  const hash = searchParams.get('hash');

  useEffect(() => {
    if (!hash || !isValidDashboardHash(hash)) {
      navigate('/', { replace: true });
      return;
    }

    fetchData();
  }, [hash, selectedLancamento]);

  const fetchData = async () => {
    try {
      const { data, error } = await supabase
        .from('respostas_pesquisa')
        .select('*')
        .eq('lancamento', selectedLancamento);

      if (error) throw error;

      setResponses(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = (field: keyof SurveyResponse, options: { [key: string]: string }) => {
    const counts: { [key: string]: number } = {};
    responses.forEach(response => {
      const value = response[field];
      if (Array.isArray(value)) {
        value.forEach(v => {
          counts[v] = (counts[v] || 0) + 1;
        });
      } else {
        counts[value as string] = (counts[value as string] || 0) + 1;
      }
    });

    const total = responses.length;
    return Object.entries(counts).map(([key, value]) => ({
      name: options[key] || key,
      value,
      percentage: (value / total) * 100
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500">Erro: {error}</p>
      </div>
    );
  }

  const experienceOptions = {
    'iniciante_total': 'Nunca programei, tô começando do zero',
    'tentei_sozinho': 'Já tentei aprender sozinho, mas fiquei travado',
    'sei_basico': 'Sei o básico, mas não consigo aplicar direito',
    'tenho_projetos': 'Já programei projetos, mas sinto que falta direção',
    'quero_especializar': 'Quero me especializar e sair do raso'
  };

  const challengesOptions = {
    'nao_sei_comecar': 'Não sei por onde começar',
    'pulando_conteudo': 'Fico pulando de conteúdo e não consigo seguir uma linha',
    'dificuldade_java': 'Não entendo bem o Java e os conceitos',
    'falta_foco': 'Tenho dificuldade de manter o foco e a prática',
    'medo_vagas': 'Tenho medo de não conseguir vaga depois'
  };

  const goalOptions = {
    'entrar_area_tech': 'Entrar na área de tecnologia',
    'mudar_carreira': 'Mudar de carreira urgentemente',
    'primeira_vaga_backend': 'Conquistar minha primeira vaga de dev backend',
    'virar_especialista': 'Me tornar um especialista valorizado',
    'criar_projetos': 'Criar meus próprios projetos com Java'
  };

  const notificationOptions = {
    'quero_prioridade': 'Sim, quero prioridade na abertura',
    'quero_bonus': 'Sim, e quero receber os bônus de quem entra rápido',
    'quero_conhecer': 'Ainda tô conhecendo, mas quero saber mais',
    'sem_interesse': 'Não tenho interesse em me inscrever agora'
  };

  const experienceData = prepareChartData('experiencia_programacao', experienceOptions);
  const challengesData = prepareChartData('desafios', challengesOptions);
  const goalData = prepareChartData('objetivo_principal', goalOptions);
  const notificationData = prepareChartData('preferencia_notificacao', notificationOptions);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard de Respostas</h1>
        
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700">
            Selecione o Lançamento:
            <select
              value={selectedLancamento}
              onChange={(e) => setSelectedLancamento(Number(e.target.value))}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value={1}>Lançamento 1</option>
              <option value={2}>Lançamento 2</option>
              <option value={3}>Lançamento 3</option>
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Experiência em Programação */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-2">Experiência em Programação</h2>
            <p className="text-gray-600 mb-4">Onde você está hoje na sua jornada com programação?</p>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={experienceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => [
                      `${value} (${props.payload.percentage.toFixed(1)}%)`,
                      'Respostas'
                    ]}
                  />
                  <Bar dataKey="value">
                    {experienceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Desafios */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-2">Desafios</h2>
            <p className="text-gray-600 mb-4">Qual desses desafios mais te trava hoje?</p>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={challengesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => [
                      `${value} (${props.payload.percentage.toFixed(1)}%)`,
                      'Respostas'
                    ]}
                  />
                  <Bar dataKey="value">
                    {challengesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Objetivo Principal */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-2">Objetivo Principal</h2>
            <p className="text-gray-600 mb-4">Qual é seu maior objetivo ao aprender Java?</p>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={goalData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => [
                      `${value} (${props.payload.percentage.toFixed(1)}%)`,
                      'Respostas'
                    ]}
                  />
                  <Bar dataKey="value">
                    {goalData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Preferência de Notificação */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-2">Preferência de Notificação</h2>
            <p className="text-gray-600 mb-4">Você gostaria de ser avisado quando as vagas da Jornada Java abrirem?</p>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={notificationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string, props: any) => [
                      `${value} (${props.payload.percentage.toFixed(1)}%)`,
                      'Respostas'
                    ]}
                  />
                  <Bar dataKey="value">
                    {notificationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Respostas de Texto */}
          <div className="bg-white p-6 rounded-lg shadow md:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-semibold">Fatores de Bloqueio</h2>
                <p className="text-gray-600 mt-1">Se eu pudesse te entregar uma coisa na Java Dev Week que desbloqueasse seu caminho, o que seria?</p>
              </div>
              <button
                onClick={() => setExpandedTexts(!expandedTexts)}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                {expandedTexts ? 'Reduzir' : 'Expandir'}
              </button>
            </div>
            <div className={`space-y-4 ${expandedTexts ? '' : 'max-h-[300px] overflow-y-auto'}`}>
              {responses.map((response, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded">
                  <p className="text-gray-700">{response.fator_bloqueio}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 